import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'antd';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import classNames from 'classnames';
import { optionToValue } from 'utils/form';
import QuestionHeader from './QuestionHeader';
import './MultiChoice.scss';

const CheckboxGroup = Checkbox.Group;

export default class MultiChoice extends Component {
  static propTypes = {
    text: PropTypes.string.isRequired,
    option: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        valueType: PropTypes.string,
      }),
    ).isRequired,
    id: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onIncomplete: PropTypes.func.isRequired,
    onAnswer: PropTypes.func.isRequired,
    required: PropTypes.bool.isRequired,
    answer: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        valueType: PropTypes.string,
      }),
    ),
    disabled: PropTypes.bool,
    isAnswerType: PropTypes.bool,
    readOnly: PropTypes.bool,
  };

  static defaultProps = {
    answer: [],
    disabled: false,
    isAnswerType: false,
    readOnly: false,
  };

  state = {
    values: map(
      Array.isArray(this.props.answer)
        ? this.props.answer
        : [this.props.answer],
      (answer) => answer.value,
    ),
  };

  componentDidMount() {
    const { id, onAnswer, onComplete, required } = this.props;
    const { values } = this.state;
    if (typeof values === 'undefined' || isEmpty(values)) {
      onAnswer(id, []);
    }
    if (!required) {
      onComplete();
    }
  }

  options = this.props.option;

  handleSelect = (values) => {
    const { onAnswer, id, onComplete, required, onIncomplete } = this.props;
    const checkedOptions = values.map((value) => ({
      ...find(this.options, ['value', value]),
    }));
    onAnswer(id, checkedOptions);
    this.setState({
      values,
    });
    if (required && isEmpty(checkedOptions)) {
      onIncomplete();
    } else {
      onComplete();
    }
  };

  optionValueFromCheckboxInput = (rawInput) => {
    const { readOnly } = this.props;
    const match = find(this.options, (option) => {
      let v = optionToValue(option).value;
      v = readOnly ? v.toString() : v;
      return String(v) === String(rawInput);
    });
    if (!match) {
      return undefined;
    }
    let v = optionToValue(match).value;
    return readOnly ? v.toString() : v;
  };

  /**
   * rc-checkbox does not forward onKeyDown to the native input; handle Enter on a parent.
   */
  isEnterKey = (e) => e.key === 'Enter' || e.key === 'NumpadEnter';

  handleCheckboxGroupKeyDown = (e) => {
    if (!this.isEnterKey(e) || e.defaultPrevented) {
      return;
    }
    const { target } = e;
    if (
      !target ||
      target.tagName !== 'INPUT' ||
      target.getAttribute('type') !== 'checkbox'
    ) {
      return;
    }
    const { disabled, readOnly } = this.props;
    if (disabled || readOnly) {
      return;
    }
    e.preventDefault();
    const optionValue = this.optionValueFromCheckboxInput(target.value);
    if (optionValue === undefined) {
      return;
    }
    const { values } = this.state;
    const isSelected = values.some((v) => v === optionValue);
    const nextValues = isSelected
      ? values.filter((v) => v !== optionValue)
      : [...values, optionValue];
    this.handleSelect(nextValues);
  };

  renderOptions = () =>
    map(this.options, (option, idx) => {
      const { id, readOnly } = this.props;
      let value = optionToValue(option).value;
      value = readOnly ? value.toString() : value;

      return (
        <Checkbox
          key={`${id}.option.items[${idx}]`}
          value={value}
          className="checkboxStyle"
        >
          {value}
        </Checkbox>
      );
    });

  render() {
    const { text, descriptor, disabled, isAnswerType, readOnly } = this.props;
    const { values } = this.state;

    return (
      <div data-testid="multiple-choice-question" className="ma3 w-80 center">
        <p
          className={classNames('b f5', isAnswerType ? 'tl' : 'tc')}
          style={{ overflowWrap: 'break-word' }}
        >
          <QuestionHeader
            linkId={this.props.id}
            text={text}
            descriptor={descriptor}
            isAnswerType={isAnswerType}
            readOnly={readOnly}
          />
        </p>
        <div onKeyDown={this.handleCheckboxGroupKeyDown}>
          <CheckboxGroup
            className="w-100 tl"
            value={values}
            onChange={this.handleSelect}
            disabled={disabled}
          >
            {this.renderOptions()}
          </CheckboxGroup>
        </div>
      </div>
    );
  }
}
